import { create } from 'zustand'
import type { Project } from '@/types'
import { getProjects } from '@/api/projects'

interface ProjectState {
  currentProject: Project | null
  projectList: Project[]

  setCurrentProject: (p: Project | null) => void
  fetchProjects: () => Promise<void>
  /** 退出登录/切换账号时清空，避免下个账号看到上个账号的项目缓存 */
  resetProjectState: () => void
}

// 当前项目 ID 持久化：刷新页面后恢复上次选中的项目（否则每次刷新回退到列表第一个）
const CURRENT_PROJECT_KEY = 'auroratest_current_project_id'

const readSavedProjectId = (): number => {
  try {
    return Number(localStorage.getItem(CURRENT_PROJECT_KEY)) || 0
  } catch {
    return 0
  }
}

const saveCurrentProjectId = (id: number | null) => {
  try {
    if (id) localStorage.setItem(CURRENT_PROJECT_KEY, String(id))
    else localStorage.removeItem(CURRENT_PROJECT_KEY)
  } catch {
    /* 隐私模式等 localStorage 不可用场景静默忽略 */
  }
}

export const useProjectStore = create<ProjectState>()((set, get) => ({
  currentProject: null,
  projectList: [],

  setCurrentProject: (p) => {
    set({ currentProject: p })
    saveCurrentProjectId(p?.id ?? null)
  },

  resetProjectState: () => {
    set({ currentProject: null, projectList: [] })
    saveCurrentProjectId(null)
  },

  fetchProjects: async () => {
    try {
      const res = await getProjects({ page: 1, page_size: 100 })
      const list = Array.isArray(res.data) ? res.data : []
      set({ projectList: list })
      const { currentProject } = get()
      if (!currentProject && list.length > 0) {
        // 刷新后恢复上次选中的项目；不可用则回退第一个
        const saved = list.find((p) => p.id === readSavedProjectId())
        set({ currentProject: saved || list[0] })
      } else if (currentProject && !list.some((p) => p.id === currentProject.id)) {
        // 当前项目已不在可见列表（被移出成员/项目被删）→ 回退到第一个
        set({ currentProject: list[0] || null })
      }
    } catch (e: unknown) {
      console.error('[ProjectStore] 获取项目列表失败:', e)
    }
  },
}))
